import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps, useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import MessageEventModal from "go-component/components/MessageEvent/MessageEventModal";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate, { defaultFormatDate } from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { isAndroidWebView } from "go-core/utils/utils";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { hasPermission, selectOrganization } from "go-security/services/organizations/selectors";
import { selectIsAdmin } from "go-security/services/users/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import FormatResourceStatus from "../../../../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	CompanyInfoApi,
	InvoiceApi,
	InvoiceDetailsApi,
	KSeFInvoiceApiStatus,
} from "../../../../../../../../../../../services/Api/Organization/types";
import ConfirmedInvoiceWarning from "../../../../../components/ConfirmedInvoiceWarning";
import CompanyInfoNotFull from "../../../../../components/Errors/CompanyInfoNotFull";
import InvoiceDetailsNotExist from "../../../../../components/Errors/InvoiceDetailsNotExist";
import ExportInvoicePdfModal from "../../../../../components/ExportInvoicePdfModal/ExportInvoicePdfModal";
import ExportInvoiceConfirmationModal from "../../../../../components/ExportInvoicesConfirmationModal/ExportInvoiceConfirmationModal";
import InvoicePdfPreviewModal from "../../../../../components/InvoicePdfPreviewModal/InvoicePdfPreviewModal";
import InvoiceCorrectionPreview from "../../../../../components/Preview/InvoiceCorrectionPreview";
import SendInvoiceByEmailModal from "../../../../../components/SendInvoiceByEmailModal/SendInvoiceByEmailModal";
import { useCreateOfflineInvoice } from "../../../../../hooks/useCreateOfflineInvoice";
import { useKSeFStatusRequests } from "../../../../../hooks/useKSeFStatusRequests";
import { exportInvoiceToPDFHandler } from "../../../../../services";
import { exportEInvoice, generateInvoicePreviewPdfLink } from "../../../../../services/exportInvoice";
import {
	ExportInvoiceModalMode,
	getDisplayInsufficientCompanyInfo,
	getDisplayInvoiceDetailsNotExist,
} from "../../../../../utils";
import { FETCH_CORRECTION_BASE_INVOICE_INCLUDES, FETCH_CORRECTION_INCLUDES } from "../../utils";

interface Props extends RouteComponentProps {
	invoiceId: string;
	correctionId?: number;
}

const OrganizationInvoicesCorrectionsPreviewPage: FC<Props> = (props) => {
	const [invoice, setInvoice] = useState<InvoiceApi | undefined>(undefined);
	const [companyInfo, setCompanyInfo] = useState<CompanyInfoApi | undefined>(undefined);
	const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetailsApi | undefined>(undefined);
	const [showLogs, setShowLogs] = useState<boolean>(false);
	const [correction, setCorrection] = useState<InvoiceApi | undefined>(undefined);
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>(undefined);
	const [showSendInvoiceByEmailModal, setShowSendInvoiceByEmailModal] = useState(false);
	const [showExportPdfInvoiceModal, setShowExportPdfInvoiceModal] = useState<ExportInvoiceModalMode>(
		ExportInvoiceModalMode.HIDDEN
	);
	const [invoicePdfLink, setInvoicePdfLink] = useState<string>("");
	const [showConfirmationExportInvoice, setShowConfirmationExportInvoice] = useState<boolean>(false);
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();
	const confirmation = useConfirmation();
	const history = useHistory();
	const { addFlash, addSuccessFlash } = useFlash();
	const hasVenueInvoiceEditPermission = useSelector(
		hasPermission(["VENUE_OWN_INVOICE_MANAGE", "VENUE_INVOICE_EDIT"])
	);
	const isMobile = useWindowSize().isMobile;
	const isAdmin = useSelector(selectIsAdmin);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const isSendToKSeFAutomatically = correction?.settings.is_send_to_ksef_automatically;
	const { KSeFServiceUnavailable, createOfflineInvoiceHandler, displayNoOfflineCertAlert } = useCreateOfflineInvoice(
		correction,
		setCorrection
	);
	const displayInsufficientCompanyInfo = getDisplayInsufficientCompanyInfo(correction, companyInfo);
	const displayInvoiceDetailsNotExist = getDisplayInvoiceDetailsNotExist(correction, invoiceDetails);
	const displayCorrectionPreview = !(displayInsufficientCompanyInfo || displayInvoiceDetailsNotExist);

	useEffect(() => {
		const invParams: Record<string, string> = { include: FETCH_CORRECTION_BASE_INVOICE_INCLUDES };
		const corrParams: Record<string, string> = { include: FETCH_CORRECTION_INCLUDES };
		const cfParams: Record<string, string> = {
			f: btoa(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}resource_type|e=INVOICE`),
			include: "options,constraints",
		};
		let inv: InvoiceApi;
		if (props.correctionId) {
			Promise.all([
				apiOrganization.getInvoice(props.correctionId, corrParams),
				apiOrganization.getCustomFields(cfParams),
				apiOrganization.getCompanyInfo(),
				apiOrganization.getInvoiceDetails(),
			])
				.then(([correctionResponse, cfResponse, companyInfo, invoiceDetails]) => {
					setCorrection(correctionResponse);
					const linkedInvoice = correctionResponse.linked_invoices.find(
						(f: { document_type: string }) => f.document_type === "INVOICE" || f.document_type === "ADVANCE"
					) as InvoiceApi | undefined;
					setInvoice(linkedInvoice);
					setCustomFieldsConfig(cfResponse);
					setCompanyInfo(companyInfo);
					setInvoiceDetails(invoiceDetails);
				})
				.catch((err) => {
					handleError.alert(err, addFlash);
				});
		} else {
			Promise.all([
				apiOrganization.getInvoice(Number(props.invoiceId), invParams),
				apiOrganization.getCustomFields(cfParams),
				apiOrganization.getCompanyInfo(),
				apiOrganization.getInvoiceDetails(),
			])
				.then(([invoiceResponse, cfResponse, companyInfo, invoiceDetails]) => {
					inv = invoiceResponse;
					setInvoice(inv);
					setCustomFieldsConfig(cfResponse);
					setCompanyInfo(companyInfo);
					setInvoiceDetails(invoiceDetails);
				})
				.then(() => {
					const correctionId = inv?.linked_invoices.find((f) => f.document_type === "CORRECTION")?.id;
					if (correctionId) {
						return apiOrganization.getInvoice(correctionId, corrParams);
					}
				})
				.then((res) => {
					setCorrection(res);
				})
				.catch((err) => {
					handleError.alert(err, addFlash);
				});
		}
	}, []);

	useEffect(() => {
		handleChangeTabTitle(
			`${t("modules.invoice.field.correction.title")} ${t("common.word.to", { ns: "lib" })} ${t(
				"common.word.invoice"
			)} ${invoice?.number || ""}`
		);
	}, [invoice?.number]);

	const refetchCorrection = async () => {
		try {
			const correctionID = correction?.id ?? props.correctionId;
			if (correctionID) {
				const fetchedCorrection = await apiOrganization.getInvoice(correctionID, {
					include: FETCH_CORRECTION_INCLUDES,
				});
				setCorrection(fetchedCorrection);
				return fetchedCorrection;
			}

			return null;
		} catch (err) {
			handleError.alert(err, addFlash);
			return null;
		}
	};

	const { manageInvoicePreviewRefetching, refetchingInvoiceInProgress, displayKSeFUninstalledAlert } =
		useKSeFStatusRequests(
			refetchCorrection,
			correction?.id ?? props.correctionId,
			correction?.settings.ksef_token_exists || correction?.settings.ksef_auth_data_exists
		);

	if (!invoice || !customFieldsConfig || !correction) {
		return <LoadingContainer />;
	}

	const removeInvoice = async (invoice: InvoiceApi) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			await apiOrganization.removeInvoice(invoice.id);
			history.push(`/${organization.id}/invoices`);
			addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const confirmInvoice = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.action"),
			});
			await apiOrganization.confirmInvoice(correction.id);
			await refetchCorrection();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const buttons: ButtonProps[] = [];

	if (!(!hasVenueInvoiceEditPermission && invoice.status === "CONFIRMED" && !isAdmin))
		buttons.push({
			title: t("common.action.edit", { ns: "lib" }),
			path: `/${organization.id}/invoices/${invoice.id}/corrections/edit`,
			variant: "light",
		});
	buttons.push({
		title: t("common.action.export_pdf", { ns: "lib" }),
		action: () =>
			exportInvoiceToPDFHandler(correction, setShowExportPdfInvoiceModal, ExportInvoiceModalMode.EXPORT_TO_PDF),
		dropdown: true,
		hide: isAndroidWebView,
	});
	buttons.push({
		title: t("common.action.print", { ns: "lib" }),
		action: () => exportInvoiceToPDFHandler(correction, setShowExportPdfInvoiceModal, ExportInvoiceModalMode.PRINT),
		dropdown: true,
	});
	buttons.push({
		title: t("common.action.preview", { ns: "lib" }),
		action: async () => {
			const link = await generateInvoicePreviewPdfLink(correction);
			setInvoicePdfLink(link);
		},
		dropdown: true,
	});
	buttons.push({
		title: t("modules.invoice.field.e_invoice.title"),
		action: () => exportEInvoice(correction, t),
		dropdown: true,
	});
	buttons.push({
		title: t("modules.invoice.action.send_invoice_by_e-mail.title"),
		action: () => setShowSendInvoiceByEmailModal(true),
		dropdown: true,
	});
	buttons.push({
		title: t("common.action.remove", { ns: "lib" }),
		action: () => removeInvoice(correction),
		dropdown: true,
	});
	buttons.push({
		title: t("lib:common.word.logs"),
		action: () => setShowLogs(true),
		dropdown: true,
	});
	buttons.push({
		title:
			correction.status === "CONFIRMED" ||
			(correction.ksef_data?.online === false && correction.ksef_data?.status !== KSeFInvoiceApiStatus.SUCCESS)
				? t("modules.invoice.action.send_to_ksef.title")
				: t("modules.invoice.action.confirm_and_send_to_KSeF.title"),
		action: manageInvoicePreviewRefetching,
		hide:
			correction.status === "CONFIRMED" &&
			correction.ksef_data &&
			correction.ksef_data.status !== KSeFInvoiceApiStatus.ERROR &&
			!(!correction.ksef_data.online && correction.ksef_data.status !== KSeFInvoiceApiStatus.SUCCESS),
		dropdown: true,
	});
	buttons.push({
		title: t("lib:common.action.confirm"),
		action: confirmInvoice,
		hide: correction.status === "CONFIRMED" || isSendToKSeFAutomatically,
		dropdown: true,
	});
	buttons.push({
		title: t("modules.invoice.action.create_invoice_in_offline_mode.title"),
		action: createOfflineInvoiceHandler,
		hide: !KSeFServiceUnavailable,
		dropdown: true,
	});

	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.edit", { ns: "lib" }),
			path: `/${organization.id}/invoices/${invoice.id}/corrections/edit`,
			hide: !hasVenueInvoiceEditPermission && invoice.status === "CONFIRMED" && !isAdmin,
		},
		{
			title: t("common.action.export_pdf", { ns: "lib" }),
			action: () =>
				exportInvoiceToPDFHandler(
					correction,
					setShowExportPdfInvoiceModal,
					ExportInvoiceModalMode.EXPORT_TO_PDF
				),
			hide: isAndroidWebView,
		},
		{
			title: t("common.action.print", { ns: "lib" }),
			action: () =>
				exportInvoiceToPDFHandler(correction, setShowExportPdfInvoiceModal, ExportInvoiceModalMode.PRINT),
		},
		{
			title: t("common.action.preview", { ns: "lib" }),
			action: async () => {
				const link = await generateInvoicePreviewPdfLink(correction);
				setInvoicePdfLink(link);
			},
		},
		{
			title: t("modules.invoice.field.e_invoice.title"),
			action: () => exportEInvoice(correction, t),
		},
		{
			title: t("modules.invoice.action.send_invoice_by_e-mail.title"),
			action: () => setShowSendInvoiceByEmailModal(true),
		},
		{
			title: t("common.action.remove", { ns: "lib" }),
			action: () => removeInvoice(correction),
		},
		{
			title: t("lib:common.word.logs"),
			action: () => setShowLogs(true),
		},
		{
			title:
				correction.status === "CONFIRMED" ||
				(correction.ksef_data?.online === false &&
					correction.ksef_data?.status !== KSeFInvoiceApiStatus.SUCCESS)
					? t("modules.invoice.action.send_to_ksef.title")
					: t("modules.invoice.action.confirm_and_send_to_KSeF.title"),
			action: manageInvoicePreviewRefetching,
			hide:
				correction.status === "CONFIRMED" &&
				correction.ksef_data &&
				correction.ksef_data.status !== KSeFInvoiceApiStatus.ERROR &&
				!(!correction.ksef_data.online && correction.ksef_data.status !== KSeFInvoiceApiStatus.SUCCESS),
		},
		{
			title: t("lib:common.action.confirm"),
			action: confirmInvoice,
			hide: correction.status === "CONFIRMED" || isSendToKSeFAutomatically,
		},
		{
			title: t("modules.invoice.action.create_invoice_in_offline_mode.title"),
			action: createOfflineInvoiceHandler,
			hide: !KSeFServiceUnavailable,
		},
	];

	const renderSubtitle = () => (
		<ul>
			<li className="me-1">{FormatDate(correction.dates?.issued_at, undefined, false, defaultFormatDate)}</li>
			<li>{FormatMoney(correction?.summary?.price_sum_gross)}</li>
		</ul>
	);

	const avatar = {
		color: "#1A86E0",
		date: invoice.dates?.issued_at,
	};

	const renderTitle = () => (
		<>
			{t("modules.invoice.field.correction.title")} {t("common.word.to", { ns: "lib" })}{" "}
			{t("common.word.invoice")} {invoice.number}
			<FormatResourceStatus status={correction.status === "OPEN" ? "NEW" : invoice.status} />
		</>
	);

	const changeInvoiceStatusToConfirmed = () => {
		setInvoice({
			...invoice,
			status: "CONFIRMED",
		});
	};

	return (
		<div className={"content-preview"}>
			{showConfirmationExportInvoice && (
				<ExportInvoiceConfirmationModal
					isShown={showConfirmationExportInvoice}
					onHide={() => setShowConfirmationExportInvoice(false)}
					invoices={[correction]}
					onSuccessAction={changeInvoiceStatusToConfirmed}
				/>
			)}
			<SendInvoiceByEmailModal
				isShown={showSendInvoiceByEmailModal}
				onHide={() => setShowSendInvoiceByEmailModal(false)}
				invoice={correction}
				onSuccess={changeInvoiceStatusToConfirmed}
			/>
			<MobileActions actions={mobileActions} />
			<Header
				buttons={isMobile ? [] : buttons}
				subtitle={renderSubtitle()}
				avatar={avatar}
				title={renderTitle()}
			/>
			{!hasVenueInvoiceEditPermission && invoice.status === "CONFIRMED" && !isAdmin && (
				<ConfirmedInvoiceWarning />
			)}
			{displayInvoiceDetailsNotExist && <InvoiceDetailsNotExist />}
			{displayInsufficientCompanyInfo && <CompanyInfoNotFull />}
			{displayCorrectionPreview && (
				<InvoiceCorrectionPreview
					correction={correction}
					setCorrection={setCorrection}
					config={customFieldsConfig}
					fetchInvoice={refetchCorrection}
					refetchingInvoiceInProgress={refetchingInvoiceInProgress}
					displayKSeFUninstalledAlert={displayKSeFUninstalledAlert}
					displayNoOfflineCertAlert={displayNoOfflineCertAlert}
				/>
			)}
			<ExportInvoicePdfModal
				mode={showExportPdfInvoiceModal}
				invoice={correction}
				refetchInvoice={refetchCorrection}
				onHide={() => setShowExportPdfInvoiceModal(ExportInvoiceModalMode.HIDDEN)}
				setInvoice={setCorrection}
			/>
			{showLogs && (
				<MessageEventModal
					path={`/${organization.id}/logs/message_events`}
					resourceId={invoice.id}
					resourceType={"INVOICE"}
					onHide={() => setShowLogs(false)}
					organizationId={organization.id}
				/>
			)}
			{invoicePdfLink && (
				<InvoicePdfPreviewModal
					show={!!invoicePdfLink}
					onHide={() => setInvoicePdfLink("")}
					pdfLink={invoicePdfLink}
					invoice={correction}
				/>
			)}
		</div>
	);
};
export default OrganizationInvoicesCorrectionsPreviewPage;
